/**
 * Scribe Store
 *
 * Manages state for the Scribe agent: phase, input text, LLM proposals,
 * per-proposal review decisions, and created item IDs.
 *
 * Phases:
 *   idle      — waiting for input
 *   parsing   — LLM call in progress
 *   reviewing — proposals ready for human review
 *   creating  — creating accepted items in DB
 *   done      — all accepted items created
 */

import { create } from 'zustand';
import { getAgentSettings, type AgentSettings } from '../../lib/agentSettingsService';
import { countAgentModelCall } from '../../lib/agentRuntimeService';
import { SCRIBE_AGENT_TYPE } from '../../lib/ai/agentDefinitions';
import { parseTextToItems } from '../../lib/llmScribeService';
import { AgentLlmError } from '../../lib/llmClient';
import type { ScribeProposedItem } from '../../lib/llmScribeService';
import type { ItemLanguageType } from '../../types/prioritization/lifecycle';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

// ── Review item (proposal + decision state) ───────────────────────────────────

export type ScribeProposalStatus = 'pending' | 'accepted' | 'editing' | 'skipped';

export interface ScribeReviewItem extends ScribeProposedItem {
  status: ScribeProposalStatus;
  /** Edited values (only set when user modifies before accepting) */
  editTitle?: string;
  editDescription?: string;
  editCategory?: ItemLanguageType;
  editAltitude?: number;
}

// ── Store state ───────────────────────────────────────────────────────────────

export type ScribePhase = 'idle' | 'parsing' | 'reviewing' | 'creating' | 'done';

interface ScribeStoreState {
  phase: ScribePhase;
  inputText: string;
  parseError: string | null;
  createError: string | null;
  proposals: ScribeReviewItem[];
  /** IDs of items that were successfully created */
  createdItemIds: string[];
  /** The Scribe agent's settings (agent_settings, agent_type 'scribe'; migration 942). */
  config: AgentSettings | null;
  /** The workspace the settings were read for — model calls are counted against it. */
  configWorkspaceId: string | null;
  isLoading: boolean;

  // ── Config actions ──────────────────────────────────────────
  fetchConfig: (workspaceId: string) => Promise<void>;

  // ── Parse actions ───────────────────────────────────────────
  setInputText: (text: string) => void;
  parseInput: (text: string, model?: string) => Promise<void>;

  // ── Review actions ──────────────────────────────────────────
  acceptProposal: (localId: string) => void;
  skipProposal: (localId: string) => void;
  startEditProposal: (localId: string) => void;
  saveEditProposal: (
    localId: string,
    edits: { title?: string; description?: string; category?: ItemLanguageType; altitude?: number },
  ) => void;
  cancelEditProposal: (localId: string) => void;
  acceptAll: () => void;

  // ── Create action ───────────────────────────────────────────
  createAccepted: (params: {
    workspaceId: string;
    userId: string;
    onCreate: (item: {
      id: string;
      title: string;
      description: string;
      category: ItemLanguageType;
      altitude: number;
      workspaceId: string;
    }) => Promise<string | null>;
  }) => Promise<void>;

  // ── Navigation ──────────────────────────────────────────────
  reset: () => void;
  backToInput: () => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useScribeStore = create<ScribeStoreState>((set, get) => ({
  phase: 'idle',
  inputText: '',
  parseError: null,
  createError: null,
  proposals: [],
  createdItemIds: [],
  config: null,
  configWorkspaceId: null,
  isLoading: false,

  // ── fetchConfig ──────────────────────────────────────────────────────────

  fetchConfig: async (workspaceId) => {
    set({ isLoading: true });
    const result = await getAgentSettings(workspaceId, SCRIBE_AGENT_TYPE);
    set({ config: result.data, configWorkspaceId: workspaceId, isLoading: false });
  },

  // ── setInputText ─────────────────────────────────────────────────────────

  setInputText: (text) => set({ inputText: text }),

  // ── parseInput ───────────────────────────────────────────────────────────

  parseInput: async (text, model) => {
    if (!text.trim()) return;
    set({ phase: 'parsing', parseError: null });

    const config = get().config;
    const effectiveModel = model ?? config?.model ?? DEFAULT_MODEL;
    const systemPrompt = config?.systemPrompt ?? null;
    const sources = { reference_docs: true, ...(config?.evidenceSources ?? {}) };

    // Every model call counts against the agent's monthly cap (spec §2 K).
    const workspaceId = get().configWorkspaceId;
    if (workspaceId) {
      const meter = await countAgentModelCall(workspaceId, SCRIBE_AGENT_TYPE);
      if (!meter.allowed) {
        set({ phase: 'idle', parseError: meter.error ?? `Scribe has used its ${meter.limit} model calls for this month. An administrator can raise the cap on its settings page.` });
        return;
      }
    }

    try {
      const items = await parseTextToItems({
        text,
        model: effectiveModel,
        systemPrompt,
        modelParams: config?.modelParams ?? null,
        referenceDocs: sources.reference_docs ? config?.referenceDocs ?? [] : [],
      });

      if (!items.length) {
        set({
          phase: 'idle',
          parseError: 'No items could be extracted from that text. Try being more specific.',
        });
        return;
      }

      const proposals: ScribeReviewItem[] = items.map((item) => ({
        ...item,
        status: 'pending',
      }));

      set({ phase: 'reviewing', proposals, parseError: null });
    } catch (err) {
      const message =
        err instanceof AgentLlmError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Scribe could not reach Peak LLM.';
      set({ phase: 'idle', parseError: message });
    }
  },

  // ── acceptProposal ───────────────────────────────────────────────────────

  acceptProposal: (localId) => {
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p._localId === localId ? { ...p, status: 'accepted' } : p,
      ),
    }));
  },

  // ── skipProposal ─────────────────────────────────────────────────────────

  skipProposal: (localId) => {
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p._localId === localId ? { ...p, status: 'skipped' } : p,
      ),
    }));
  },

  // ── startEditProposal ────────────────────────────────────────────────────

  startEditProposal: (localId) => {
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p._localId === localId
          ? {
              ...p,
              status: 'editing',
              editTitle: p.editTitle ?? p.title,
              editDescription: p.editDescription ?? p.description,
              editCategory: p.editCategory ?? p.category,
              editAltitude: p.editAltitude ?? p.altitude,
            }
          : p,
      ),
    }));
  },

  // ── saveEditProposal ─────────────────────────────────────────────────────

  saveEditProposal: (localId, edits) => {
    set((s) => ({
      proposals: s.proposals.map((p) => {
        if (p._localId !== localId) return p;
        return {
          ...p,
          status: 'accepted',
          editTitle: edits.title ?? p.editTitle ?? p.title,
          editDescription: edits.description ?? p.editDescription ?? p.description,
          editCategory: edits.category ?? p.editCategory ?? p.category,
          editAltitude: edits.altitude ?? p.editAltitude ?? p.altitude,
        };
      }),
    }));
  },

  // ── cancelEditProposal ───────────────────────────────────────────────────

  cancelEditProposal: (localId) => {
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p._localId === localId ? { ...p, status: 'pending' } : p,
      ),
    }));
  },

  // ── acceptAll ────────────────────────────────────────────────────────────

  acceptAll: () => {
    set((s) => ({
      proposals: s.proposals.map((p) =>
        p.status === 'pending' ? { ...p, status: 'accepted' } : p,
      ),
    }));
  },

  // ── createAccepted ───────────────────────────────────────────────────────

  createAccepted: async ({ workspaceId, userId: _userId, onCreate }) => {
    const accepted = get().proposals.filter((p) => p.status === 'accepted');
    if (!accepted.length) return;

    set({ phase: 'creating', createError: null });

    const createdIds: string[] = [];

    for (const proposal of accepted) {
      const title = proposal.editTitle ?? proposal.title;
      const description = proposal.editDescription ?? proposal.description;
      const category = proposal.editCategory ?? proposal.category;
      const altitude = proposal.editAltitude ?? proposal.altitude;

      try {
        const id = await onCreate({ id: '', title, description, category, altitude, workspaceId });
        if (id) createdIds.push(id);
      } catch {
        // Continue with remaining items; report error at end
      }
    }

    if (!createdIds.length) {
      set({ phase: 'reviewing', createError: 'No items were created. Check your permissions.' });
      return;
    }

    set({ phase: 'done', createdItemIds: createdIds, createError: null });
  },

  // ── reset ────────────────────────────────────────────────────────────────

  reset: () =>
    set({
      phase: 'idle',
      inputText: '',
      parseError: null,
      createError: null,
      proposals: [],
      createdItemIds: [],
    }),

  // ── backToInput ──────────────────────────────────────────────────────────

  backToInput: () =>
    set({
      phase: 'idle',
      parseError: null,
      proposals: [],
    }),
}));
